import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { AxiosInterceptorService } from "src/app/header-interceptor";
import { UserService } from "../core/user.service";

export interface InlineEditSaveEvent {
  tableName: string;
  fieldName: string;
  salesOrderId: number;
  recordId?: number;
  newValue: any;
}

@Component({
  standalone: false,
  selector: "app-inline-edit-cell",
  templateUrl: "./inline-edit-cell.component.html",
  styleUrls: ["./inline-edit-cell.component.scss"],
})
export class InlineEditCellComponent implements OnChanges {
  private readonly genericInlineEditEndpoint =
    "GenericInlineEditor/UpdateGenericInlineUpdate";
  private readonly legacySalesOrderEndpoint = "SalesOrder/UpdateSalesOrderField";

  @Input() value: any;
  @Input() salesOrderId: number;
  @Input() recordId: number;
  @Input() tableName = "SalesOrder";
  @Input() fieldName = "";
  @Input() fieldType = "";
  @Input() placeholder = "";
  @Input() emptyText = "-";
  @Input() canEdit = true;
  @Input() trimValue = true;
  @Input() showNotifications = true;
  @Input() apiEndpoint = "";
  @Input() displayAsTextbox = false;
  @Input() displayAsDxTextbox = false;
  @Input() showEditOnHover = false;
  @Input() minNumber: number | null = null;
  @Input() showClearButton = true;
  @Input() useSelectEditor = false;
  @Input() selectDisplayExpr = "";
  @Input() selectValueExpr = "";
  @Input() acceptCustomValue = false;
  @Input() editorContext: any;
  @Input() optionsLoader: ((context: any) => Promise<any[]>) | null = null;
  @Input() validationPattern: RegExp | string | null = null;
  @Input() validationMessage = "Please enter a valid value.";

  @Output() saved = new EventEmitter<InlineEditSaveEvent>();
  @Output() canceled = new EventEmitter<void>();

  isEditing = false;
  isSaving = false;
  draftValue: any = "";
  displayValue = "";
  editorOptions: any[] = [];
  filteredEditorOptions: any[] = [];
  isLoadingOptions = false;

  constructor(
    private api: AxiosInterceptorService,
    private userService: UserService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.value) {
      this.displayValue = this.formatValue(this.value);
    }
  }

  startEdit(): void {
    if (!this.canEdit || this.isSaving) {
      return;
    }
    this.isEditing = true;
    this.draftValue = this.isNumberField()
      ? this.toNumberValue(this.value)
      : this.value ?? "";
    if (this.useSelectEditor) {
      this.loadEditorOptions();
    }
  }

  cancelEdit(): void {
    if (this.isSaving) {
      return;
    }
    this.isEditing = false;
    this.draftValue = this.value ?? "";
    this.canceled.emit();
  }

  async save(): Promise<void> {
    if (this.isSaving || !this.isEditing) {
      return;
    }

    const normalizedValue = this.normalizeValue(this.draftValue);
    if (this.isNumberField() && normalizedValue === null) {
      this.notifyError("Please enter a valid number.");
      return;
    }
    if (
      this.isNumberField() &&
      this.minNumber !== null &&
      normalizedValue < this.minNumber
    ) {
      this.notifyError(`Value must be at least ${this.minNumber}.`);
      return;
    }
    if (!this.isValidPatternValue(normalizedValue)) {
      this.notifyError(this.validationMessage);
      return;
    }
    if (this.isSameValue(normalizedValue, this.value)) {
      this.isEditing = false;
      return;
    }
    if (
      this.useSelectEditor &&
      !this.acceptCustomValue &&
      !this.isAvailableSelectValue(normalizedValue)
    ) {
      this.notifyError("Please select a value from the available options.");
      return;
    }

    const resolvedEndpoint = this.resolveApiEndpoint();
    const resolvedRecordId = this.toRecordId(this.recordId ?? this.salesOrderId);

    if (!resolvedEndpoint || !this.tableName || !this.fieldName || !resolvedRecordId) {
      this.notifyError("Inline edit configuration is incomplete.");
      return;
    }

    this.isSaving = true;
    const payload = this.buildPayload(normalizedValue, resolvedRecordId);

    try {
      const response = await this.api.post(resolvedEndpoint, payload);
      if (response?.data?.completed) {
        this.value = normalizedValue;
        this.displayValue = this.formatValue(normalizedValue);
        this.isEditing = false;
        this.saved.emit({
          tableName: this.tableName,
          fieldName: this.fieldName,
          salesOrderId: this.salesOrderId,
          recordId: resolvedRecordId,
          newValue: normalizedValue,
        });
        if (this.showNotifications) {
          this.userService.showSnackBar(response?.data?.message || "Updated successfully.");
        }
      } else {
        this.notifyError(response?.data?.message || "Unable to update value.");
      }
    } catch (error) {
      this.notifyError(error?.message || "Unable to update value.");
    } finally {
      this.isSaving = false;
    }
  }

  onKeyDown(event: any): void {
    const key = event?.event?.key ?? event?.key;
    if (key === "Escape") {
      this.cancelEdit();
    }
  }

  private normalizeValue(value: any): any {
    if (this.isNumberField()) {
      return this.toNumberValue(value);
    }
    if (typeof value === "string") {
      return this.trimValue ? value.trim() : value;
    }
    return value;
  }

  onSelectDraftChanged(value: any): void {
    this.draftValue = value;
    this.filterEditorOptions(value);
  }

  openSelectOptions(trigger: any): void {
    this.filteredEditorOptions = [...this.editorOptions];
    trigger?.openPanel();
  }

  getSelectOptionValue(option: any): any {
    return this.selectValueExpr && option && typeof option === "object"
      ? option[this.selectValueExpr]
      : option;
  }

  getSelectOptionLabel(option: any): string {
    const value =
      this.selectDisplayExpr && option && typeof option === "object"
        ? option[this.selectDisplayExpr]
        : option;
    return value === null || value === undefined ? "" : String(value);
  }

  private filterEditorOptions(query: any): void {
    const normalizedQuery = String(query ?? "").trim().toLowerCase();
    this.filteredEditorOptions = !normalizedQuery
      ? [...this.editorOptions]
      : this.editorOptions.filter((option) =>
          this.getSelectOptionLabel(option)
            .toLowerCase()
            .includes(normalizedQuery)
        );
  }

  private async loadEditorOptions(): Promise<void> {
    if (!this.optionsLoader) {
      return;
    }
    this.isLoadingOptions = true;
    try {
      this.editorOptions = (await this.optionsLoader(this.editorContext)) || [];
      this.filterEditorOptions(this.draftValue);
    } catch {
      this.editorOptions = [];
      this.filteredEditorOptions = [];
    } finally {
      this.isLoadingOptions = false;
    }
  }

  private isNumberField(): boolean {
    return this.fieldType.toLowerCase() === "number";
  }

  private isAvailableSelectValue(value: any): boolean {
    return this.editorOptions.some((option) =>
      this.isSameValue(this.normalizeValue(this.getSelectOptionValue(option)), value)
    );
  }

  private isValidPatternValue(value: any): boolean {
    if (
      !this.validationPattern ||
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return true;
    }

    const pattern =
      this.validationPattern instanceof RegExp
        ? this.validationPattern
        : new RegExp(this.validationPattern);
    pattern.lastIndex = 0;
    return pattern.test(String(value));
  }

  private toNumberValue(value: any): number | null {
    if (value === null || value === undefined || value === "") {
      return null;
    }
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  private isSameValue(nextValue: any, currentValue: any): boolean {
    if (nextValue === currentValue) {
      return true;
    }
    if (
      this.isNumberField() &&
      nextValue !== null &&
      currentValue !== null &&
      currentValue !== undefined &&
      currentValue !== ""
    ) {
      return Number(nextValue) === Number(currentValue);
    }
    const isNextEmpty = nextValue === null || nextValue === undefined || nextValue === "";
    const isCurrentEmpty = currentValue === null || currentValue === undefined || currentValue === "";
    return isNextEmpty && isCurrentEmpty;
  }

  private formatValue(value: any): string {
    if (value === null || value === undefined || value === "") {
      return this.emptyText;
    }
    return String(value);
  }

  private resolveApiEndpoint(): string {
    if (
      !this.apiEndpoint ||
      this.apiEndpoint === this.legacySalesOrderEndpoint ||
      this.apiEndpoint === `/${this.legacySalesOrderEndpoint}` ||
      this.apiEndpoint === this.genericInlineEditEndpoint ||
      this.apiEndpoint === `/${this.genericInlineEditEndpoint}`
    ) {
      return this.genericInlineEditEndpoint;
    }

    return this.apiEndpoint;
  }

  private buildPayload(value: any, recordId: number): any {
    if (this.resolveApiEndpoint() === this.genericInlineEditEndpoint) {
      return {
        tableName: this.tableName,
        fieldName: this.fieldName,
        fieldValue: this.toGenericFieldValue(value),
        fieldType: this.fieldType || this.inferFieldType(value),
        recordId,
      };
    }

    return {
      TableName: this.tableName,
      FieldName: this.fieldName,
      SalesOrderId: recordId,
      NewValue: value,
    };
  }

  private inferFieldType(value: any): string {
    if (value instanceof Date) {
      return "date";
    }
    switch (typeof value) {
      case "number":
        return "number";
      case "boolean":
        return "boolean";
      default:
        return "string";
    }
  }

  private toGenericFieldValue(value: any): string {
    if (value === null || value === undefined) {
      return "";
    }
    if (value instanceof Date) {
      return value.toISOString();
    }
    return String(value);
  }

  private toRecordId(value: any): number {
    const parsedValue = Number.parseInt(String(value), 10);
    return Number.isNaN(parsedValue) ? 0 : parsedValue;
  }

  private notifyError(message: string): void {
    if (this.showNotifications) {
      this.userService.showSnackBar(message);
    }
  }
}
